// Demo booking slots.
//
// Slots are wall-clock times in HOST_TIMEZONE (where demos are actually run),
// converted to real instants here so a visitor in any timezone books the same
// moment. The Cloud Function `collavueBookDemo` validates against the same
// list — keep SLOT_TIMES and HOST_TIMEZONE in sync with functions/index.js.

export const HOST_TIMEZONE = "America/New_York";

export const SLOT_TIMES = [
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
];

/** Minimum lead time before a slot can be booked. Mirrors the server. */
export const MIN_LEAD_MS = 30 * 60 * 1000;

const pad = (n) => String(n).padStart(2, "0");

/** Wall-clock parts of an instant as seen in `timeZone`. */
function zonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
    .formatToParts(date)
    .reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // Some ICU builds emit "24" for midnight under hour12:false.
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** Offset of `timeZone` from UTC, in ms, at the given instant. */
function tzOffsetMs(date, timeZone) {
  const p = zonedParts(date, timeZone);
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUTC - date.getTime();
}

/** The visitor's IANA timezone, or the host zone if the browser won't say. */
export function visitorTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || HOST_TIMEZONE;
  } catch {
    return HOST_TIMEZONE;
  }
}

/** "YYYY-MM-DD" for an instant as seen in `timeZone`. */
export function zonedDateKey(date, timeZone = HOST_TIMEZONE) {
  const p = zonedParts(date, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/**
 * The real instant of `HH:MM` on `YYYY-MM-DD` in the host timezone. Two
 * passes because the offset itself depends on the instant we're solving for,
 * which only matters on the two DST-shift days a year.
 */
export function slotInstant(dateKey, time) {
  const [y, m, d] = String(dateKey).split("-").map(Number);
  const [hh, mm] = String(time).split(":").map(Number);
  const naive = Date.UTC(y, m - 1, d, hh, mm, 0);
  let ts = naive - tzOffsetMs(new Date(naive), HOST_TIMEZONE);
  ts = naive - tzOffsetMs(new Date(ts), HOST_TIMEZONE);
  return new Date(ts);
}

/** The next `count` weekdays, starting today, as pickable day objects. */
export function upcomingWeekdays(count = 10) {
const tomorrowKey = zonedDateKey(new Date(Date.now() + 86400000));
  const [y, m, d] = tomorrowKey.split("-").map(Number);
  // Anchor at noon UTC so day-stepping never trips over a DST shift.
  let cursor = Date.UTC(y, m - 1, d, 12, 0, 0);
  const days = [];

  while (days.length < count) {
    const dt = new Date(cursor);
    const dow = dt.getUTCDay();
    if (dow !== 0 && dow !== 6) {
      days.push({
        key: `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(
          dt.getUTCDate()
        )}`,
        weekday: new Intl.DateTimeFormat("en-US", {
          timeZone: "UTC",
          weekday: "short",
        }).format(dt),
        day: String(dt.getUTCDate()),
        month: new Intl.DateTimeFormat("en-US", {
          timeZone: "UTC",
          month: "short",
        }).format(dt),
        isTommorrow: days.length === 0 && `${dt.getUTCFullYear()}-${pad(
          dt.getUTCMonth() + 1
        )}-${pad(dt.getUTCDate())}` === tomorrowKey,
      });
    }
    cursor += 24 * 60 * 60 * 1000;
  }
  return days;
}

function formatTime(date, timeZone) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "";
  }
}

/** Long, unambiguous rendering used in confirmations. */
export function formatSlotLong(date, timeZone) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "long",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

/**
 * Bookable slots for a day. Past slots and ones already claimed come back
 * flagged rather than removed, so the grid keeps a stable shape.
 */
export function slotsForDay(dateKey, { taken = [], now = Date.now() } = {}) {
  const tz = visitorTimezone();
  const showLocal = tz !== HOST_TIMEZONE;
  return SLOT_TIMES.map((time) => {
    const at = slotInstant(dateKey, time);
    return {
      time,
      iso: at.toISOString(),
      at,
      label: formatTime(at, HOST_TIMEZONE),
      localLabel: showLocal ? formatTime(at, tz) : null,
      taken: taken.includes(time),
      past: at.getTime() < now + MIN_LEAD_MS,
    };
  });
}
